import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import AdminDialog from "../../components/admin/AdminDialog";

const DEPARTMENTS = ["", "women", "men", "kids", "accessories"];
const blank = {
  name: "", slug: "", department_slug: "", applicable_category_ids: [],
  unit: "inches", columns: ["Bust", "Waist", "Hip", "Length"],
  rows: [{ size: "S", values: [34, 28, 36, 40] }, { size: "M", values: [36, 30, 38, 41] }],
  notes: "", is_active: true,
};
const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

export default function AdminSizeCharts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/size-charts");
      setRows(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load size charts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId(null); setForm(blank); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      ...blank, ...c,
      columns: c.columns?.length ? c.columns : blank.columns,
      rows: c.rows?.length ? c.rows : blank.rows,
      applicable_category_ids: c.applicable_category_ids || [],
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    const body = { ...form, slug: form.slug || slugify(form.name) };
    try {
      if (editingId) await api.put(`/admin/size-charts/${editingId}`, body);
      else await api.post("/admin/size-charts", body);
      toast.success(editingId ? "Updated" : "Created");
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this size chart?")) return;
    await api.delete(`/admin/size-charts/${id}`);
    load();
  };

  const addColumn = () => setForm((f) => ({
    ...f, columns: [...f.columns, `Col${f.columns.length + 1}`],
    rows: f.rows.map((r) => ({ ...r, values: [...r.values, 0] })),
  }));
  const removeColumn = (idx) => setForm((f) => ({
    ...f, columns: f.columns.filter((_, i) => i !== idx),
    rows: f.rows.map((r) => ({ ...r, values: r.values.filter((_, i) => i !== idx) })),
  }));
  const addRow = () => setForm((f) => ({
    ...f, rows: [...f.rows, { size: "", values: f.columns.map(() => 0) }],
  }));
  const removeRow = (idx) => setForm((f) => ({ ...f, rows: f.rows.filter((_, i) => i !== idx) }));

  return (
    <div data-testid="admin-size-charts-page">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Catalog Master</div>
          <h1 className="font-display text-4xl">Size Charts</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">Reusable measurement grids attached to product categories.</p>
        </div>
        <button className="kb-btn-primary" onClick={openNew} data-testid="chart-add-btn">
          <Plus size={16} className="inline mr-1" /> New Size Chart
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {loading ? (
          <div className="col-span-2 bg-white border border-[hsl(var(--kb-border))] p-8 text-center text-[hsl(var(--kb-muted))]" data-testid="charts-loading">Loading size charts…</div>
        ) : error ? (
          <div className="col-span-2 bg-white border border-red-300 p-8 text-center text-red-700" data-testid="charts-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="col-span-2 bg-white border border-[hsl(var(--kb-border))] p-8 text-center text-[hsl(var(--kb-muted))]">
            No size charts yet.
          </div>
        ) : rows.map((c) => (
          <div key={c.id} className="bg-white border border-[hsl(var(--kb-border))] p-5" data-testid={`chart-row-${c.slug}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="font-display text-xl">{c.name}</div>
                <div className="text-xs text-[hsl(var(--kb-muted))]">
                  {c.department_slug || "all departments"} · {c.unit} · {c.rows?.length || 0} sizes × {c.columns?.length || 0} metrics
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(c)} className="kb-btn-outline !py-1 !px-2 !text-[10px]" data-testid={`chart-edit-${c.slug}`}><Pencil size={12} /></button>
                <button onClick={() => remove(c.id)} className="text-red-700 p-1" data-testid={`chart-del-${c.slug}`}><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead><tr className="bg-[hsl(var(--kb-surface-alt))]">
                  <th className="p-2 border border-[hsl(var(--kb-border))]">Size</th>
                  {(c.columns || []).map((col, i) => <th key={i} className="p-2 border border-[hsl(var(--kb-border))]">{col}</th>)}
                </tr></thead>
                <tbody>
                  {(c.rows || []).map((r, i) => (
                    <tr key={i}>
                      <td className="p-2 border border-[hsl(var(--kb-border))] font-medium">{r.size}</td>
                      {(r.values || []).map((v, j) => <td key={j} className="p-2 border border-[hsl(var(--kb-border))]">{v}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {c.notes && <div className="mt-3 text-xs text-[hsl(var(--kb-muted))]">{c.notes}</div>}
          </div>
        ))}
      </div>

      <AdminDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Size Chart"
        subtitle={editingId ? "Edit" : "New"}
        maxWidth="max-w-3xl"
        testid="chart-dialog"
        footer={
          <>
            <button className="kb-btn-outline" onClick={() => setDialogOpen(false)}>Cancel</button>
            <button className="kb-btn-primary" onClick={save} data-testid="chart-save-btn">{editingId ? "Update" : "Create"}</button>
          </>
        }
      >
        <div className="grid md:grid-cols-3 gap-3">
          <input className="kb-input" placeholder="Name (e.g. Saree — Free)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="chart-name" />
          <input className="kb-input" placeholder="Slug (auto)" value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} />
          <select className="kb-input" value={form.department_slug} onChange={(e) => setForm({ ...form, department_slug: e.target.value })} data-testid="chart-dept">
            {DEPARTMENTS.map((d) => <option key={d} value={d}>{d || "All departments"}</option>)}
          </select>
          <select className="kb-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
            <option value="inches">Inches</option>
            <option value="cm">Centimeters</option>
          </select>
          <label className="flex items-center gap-2 text-sm pt-5">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active
          </label>
        </div>

        <div className="border border-[hsl(var(--kb-border))] p-3 overflow-x-auto">
          <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
            <div className="overline">Measurement Grid</div>
            <div className="flex gap-2">
              <button onClick={addColumn} className="kb-btn-outline !py-1 !px-2 !text-[10px]" data-testid="chart-add-col"><Plus size={10} className="inline" /> Column</button>
              <button onClick={addRow} className="kb-btn-outline !py-1 !px-2 !text-[10px]" data-testid="chart-add-row"><Plus size={10} className="inline" /> Row</button>
            </div>
          </div>
          <table className="w-full text-xs">
            <thead><tr className="bg-[hsl(var(--kb-surface-alt))]">
              <th className="p-1 border border-[hsl(var(--kb-border))] w-28">Size</th>
              {form.columns.map((col, i) => (
                <th key={i} className="p-1 border border-[hsl(var(--kb-border))]">
                  <div className="flex items-center gap-1">
                    <input className="kb-input !py-1 !px-2 !text-xs" value={col} onChange={(e) => {
                      const nc = [...form.columns]; nc[i] = e.target.value;
                      setForm({ ...form, columns: nc });
                    }} />
                    <button onClick={() => removeColumn(i)} className="text-red-600" aria-label="remove column"><X size={12} /></button>
                  </div>
                </th>
              ))}
            </tr></thead>
            <tbody>
              {form.rows.map((r, ri) => (
                <tr key={ri}>
                  <td className="p-1 border border-[hsl(var(--kb-border))]">
                    <div className="flex items-center gap-1">
                      <input className="kb-input !py-1 !px-2 !text-xs" value={r.size} onChange={(e) => {
                        const nr = [...form.rows]; nr[ri] = { ...r, size: e.target.value };
                        setForm({ ...form, rows: nr });
                      }} />
                      <button onClick={() => removeRow(ri)} className="text-red-600" aria-label="remove row"><X size={12} /></button>
                    </div>
                  </td>
                  {form.columns.map((_, ci) => (
                    <td key={ci} className="p-1 border border-[hsl(var(--kb-border))]">
                      <input type="number" step="0.1" className="kb-input !py-1 !px-2 !text-xs" value={r.values[ci] ?? 0} onChange={(e) => {
                        const nr = [...form.rows]; const nv = [...r.values];
                        nv[ci] = e.target.value === "" ? 0 : Number(e.target.value);
                        nr[ri] = { ...r, values: nv };
                        setForm({ ...form, rows: nr });
                      }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <textarea className="kb-input" rows={2} placeholder="Notes / measuring tips (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </AdminDialog>
    </div>
  );
}
